import { type FormEvent, useEffect, useState } from 'react';
import { useApi, useConsulta } from '@plataforma/web/api';
import { etiqueta, fechaCorta, fechaHoraLocal, mxn } from '../formato';
import { usePuede } from '../permisos';
import type { Dictamen, Resumen, Vehiculo } from '../paginas/tipos';

const CLASE = { viable: 'exito', revisar: 'aviso', no_viable: 'peligro' } as const;
const CLASE_PUNTO: Record<string, string> = {
  ok: 'exito',
  revisar: 'aviso',
  falla: 'peligro',
  pendiente: '',
  no_aplica: '',
};
const usd = (c: string | null) =>
  c ? `USD ${Number(c).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '—';
const hoy = () => fechaHoraLocal(new Date()).slice(0, 10);

/** Resultado de un dictamen con sus puntos (arquitectura 2.15, sección 6). */
export function ResultadoDictamen({ d }: { d: Dictamen }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <span
          className={`etiqueta ${CLASE[d.resultado]}`}
          style={{ fontSize: 14, padding: '4px 12px' }}
        >
          {etiqueta(d.resultado)}
        </span>{' '}
        <span className="subtitulo">
          {fechaCorta(d.fecha)}
          {d.versiones.tipoImportacion ? ` · vía ${d.versiones.tipoImportacion}` : ''}
        </span>
      </div>
      <div className="tarjetas">
        <div className="tarjeta">
          <div className="rotulo">Puja máxima</div>
          <div className="cifra">{usd(d.pujaMaximaUsd)}</div>
          <div className="nota">
            Techo {mxn(d.techoMxn)} − margen {mxn(d.margenObjetivoMxn)}
          </div>
        </div>
        <div className="tarjeta">
          <div className="rotulo">Puja sugerida</div>
          <div className="cifra">{usd(d.pujaSugeridaUsd)}</div>
          <div className="nota">Número no redondo para el proxy bid</div>
        </div>
        <div className="tarjeta">
          <div className="rotulo">Costo total estimado</div>
          <div className="cifra">{d.costoTotalMxn ? mxn(d.costoTotalMxn) : '—'}</div>
          <div className="nota">
            {d.desglose?.desglose?.aproximado
              ? 'Valor en aduana aproximado'
              : 'Con cotización del agente'}
          </div>
        </div>
        <div className="tarjeta">
          <div className="rotulo">Margen esperado</div>
          <div
            className="cifra"
            style={{ color: d.margenEsperadoMxn?.startsWith('-') ? 'var(--peligro)' : undefined }}
          >
            {d.margenEsperadoMxn ? mxn(d.margenEsperadoMxn) : '—'}
          </div>
          <div className="nota">Techo − costo total</div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Punto</th>
            <th>Estado</th>
            <th>Detalle</th>
          </tr>
        </thead>
        <tbody>
          {d.puntos.map((p, i) => (
            <tr key={`${p.clave}-${i}`}>
              <td>{p.nombre}</td>
              <td>
                <span className={`etiqueta ${CLASE_PUNTO[p.estado] ?? ''}`}>
                  {p.estado === 'no_aplica' ? 'no aplica' : p.estado}
                </span>
              </td>
              <td>{p.detalle}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {d.desglose?.vias?.length ? (
        <p className="subtitulo">
          Vías probadas:{' '}
          {d.desglose.vias
            .map(
              (v) =>
                `${v.nombre}${v.clavesPedimento?.length ? ` (pedimento ${v.clavesPedimento.map((c) => c.clave).join('/')})` : ''}: ${v.semaforo.color}${v.sinRegla ? ' (sin regla)' : ''}`,
            )
            .join(' · ')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Dictamen de compra (arquitectura 2.15, sección 6): la plataforma llena el checklist previo a
 * la puja con el lote, la tabla de cargos, la regla vigente, la cotización del agente y la
 * valuación; el lotero solo da el FIX, los traslados y su margen mínimo.
 */
export function DictamenVehiculo({
  v,
  resumen,
  alGuardar,
}: {
  v: Vehiculo;
  resumen: Resumen | null;
  alGuardar: () => void;
}) {
  const api = useApi();
  const permitido = usePuede();
  const dictamenes = useConsulta<Dictamen[]>(`/sourcing/lotes/${v.id}/dictamen`);
  const negocio = useConsulta<{ inscritoEnPadron: boolean | null }>('/platform/mi-lotero/negocio');
  const [fix, setFix] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [calculando, setCalculando] = useState(false);
  const [abierto, setAbierto] = useState(false);
  useEffect(() => {
    api<{ valor: string }>('GET', `/finance/tipos-cambio/${hoy()}`)
      .then((t) => setFix(t.valor))
      .catch(() => setFix(''));
  }, [api]);
  const ultimo = dictamenes.datos?.[0];
  const sinLote = dictamenes.estadoError === 404;
  const enDosSemanas = fechaHoraLocal(new Date(Date.now() + 14 * 86_400_000)).slice(0, 10);

  const dictaminar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const num = (k: string) => String(f.get(k) ?? '').trim() || '0';
    const opcional = (k: string) => String(f.get(k) ?? '').trim();
    setCalculando(true);
    setError(null);
    try {
      await api('POST', `/sourcing/lotes/${v.id}/dictamen`, {
        fecha: hoy(),
        tipoCambio: num('tipoCambio'),
        fechaCruce: f.get('fechaCruce'),
        trasladoUsd: num('trasladoUsd'),
        trasladoMxn: num('trasladoMxn'),
        tramitesUsd: num('tramitesUsd'),
        costosVentaMxn: num('costosVentaMxn'),
        honorariosAgenteMxn: num('honorariosAgenteMxn'),
        valorEsperadoVentaMxn: num('valorEsperadoVentaMxn'),
        ...(opcional('valorReferenciaMxn') && {
          valorReferenciaMxn: opcional('valorReferenciaMxn'),
        }),
        margenObjetivoMxn: num('margenObjetivoMxn'),
        margenMinimoPorcentaje: opcional('margenMinimoPorcentaje') || '20',
        diasAlmacenaje: Number(f.get('diasAlmacenaje') || 0),
        exportacion: f.get('exportacion') !== 'off',
        ...(opcional('valorNadaMxn') && { valorNadaMxn: opcional('valorNadaMxn') }),
        ...(opcional('reparacionEstimadaMxn') && {
          reparacionEstimadaMxn: opcional('reparacionEstimadaMxn'),
        }),
        padronImportadores: negocio.datos?.inscritoEnPadron ?? null,
        vinValido: v.vinVerificado,
        ...(v.marca && { marca: v.marca }),
        ...(v.modelo && { modelo: v.modelo }),
        ...(v.version && { version: v.version }),
      });
      dictamenes.recargar();
      setAbierto(false);
      alGuardar();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCalculando(false);
    }
  };

  return (
    <section className="seccion">
      <header>
        <h2>Dictamen de compra</h2>
        <div style={{ flex: 1 }} />
        {!sinLote && permitido('compras.registrar') && (
          <button className="boton primario" onClick={() => setAbierto((x) => !x)}>
            {abierto ? 'Cancelar' : ultimo ? 'Recalcular dictamen' : 'Dictaminar'}
          </button>
        )}
      </header>
      {sinLote && <p className="vacio">Registra primero el lote para dictaminarlo.</p>}
      {dictamenes.error && !sinLote && (
        <p className="error" style={{ margin: 16 }}>
          {dictamenes.error}
        </p>
      )}
      {error && (
        <p className="error" style={{ margin: 16 }}>
          {error}
        </p>
      )}
      {abierto && (
        <form className="cuerpo formulario" onSubmit={(e) => void dictaminar(e)}>
          <label className="campo">
            <span>Tipo de cambio (FIX)</span>
            <input name="tipoCambio" required inputMode="decimal" defaultValue={fix} />
          </label>
          <label className="campo">
            <span>Fecha estimada de cruce</span>
            <input name="fechaCruce" type="date" required defaultValue={enDosSemanas} />
          </label>
          <label className="campo">
            <span>Valor esperado de venta (MXN)</span>
            <input
              name="valorEsperadoVentaMxn"
              required
              inputMode="decimal"
              defaultValue={resumen?.valorEsperadoVentaMxn ?? ''}
            />
          </label>
          <label className="campo">
            <span>Valor de referencia (Libro Azul o KBB, MXN)</span>
            <input
              name="valorReferenciaMxn"
              inputMode="decimal"
              placeholder="Techo del precio realista"
            />
          </label>
          <label className="campo">
            <span>Margen objetivo (MXN)</span>
            <input name="margenObjetivoMxn" inputMode="decimal" defaultValue="0" />
          </label>
          <label className="campo">
            <span>Margen mínimo (%)</span>
            <input name="margenMinimoPorcentaje" inputMode="decimal" defaultValue="20" />
          </label>
          <label className="campo">
            <span>Traslado en EE. UU. (USD)</span>
            <input name="trasladoUsd" inputMode="decimal" defaultValue="0" />
          </label>
          <label className="campo">
            <span>Traslado en México (MXN)</span>
            <input name="trasladoMxn" inputMode="decimal" defaultValue="0" />
          </label>
          <label className="campo">
            <span>Trámites en EE. UU. (USD)</span>
            <input name="tramitesUsd" inputMode="decimal" defaultValue="0" />
          </label>
          <label className="campo">
            <span>Honorarios del agente (MXN)</span>
            <input name="honorariosAgenteMxn" inputMode="decimal" defaultValue="0" />
          </label>
          <label className="campo">
            <span>Costos de venta (MXN)</span>
            <input name="costosVentaMxn" inputMode="decimal" defaultValue="0" />
          </label>
          <label className="campo">
            <span>Reparación estimada si no está en el lote (MXN)</span>
            <input name="reparacionEstimadaMxn" inputMode="decimal" />
          </label>
          <label className="campo">
            <span>Días de almacenaje</span>
            <input name="diasAlmacenaje" type="number" min={0} defaultValue={0} />
          </label>
          <label className="campo">
            <span>Valor NADA (clásicos, MXN)</span>
            <input name="valorNadaMxn" inputMode="decimal" />
          </label>
          <label className="campo" style={{ flexDirection: 'row', alignItems: 'center' }}>
            <input name="exportacion" type="checkbox" defaultChecked style={{ minHeight: 0 }} />
            <span>Se exporta (exención del sales tax)</span>
          </label>
          <div className="acciones">
            <button className="boton primario" disabled={calculando}>
              {calculando ? 'Calculando…' : 'Calcular dictamen'}
            </button>
          </div>
        </form>
      )}
      {ultimo && (
        <div className="cuerpo">
          <ResultadoDictamen d={ultimo} />
        </div>
      )}
      {dictamenes.datos && dictamenes.datos.length > 1 && (
        <p className="subtitulo" style={{ margin: '0 16px 16px' }}>
          Anteriores:{' '}
          {dictamenes.datos
            .slice(1)
            .map((d) => `${fechaCorta(d.creadoEn)} ${etiqueta(d.resultado)}`)
            .join(' · ')}
        </p>
      )}
      {!ultimo && !sinLote && !abierto && <p className="vacio">Sin dictamen todavía.</p>}
    </section>
  );
}
